import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import AddressMarkers from 'common/components/AddressMarkers';
import {Control, ControlPosition, Geometry} from 'common/components/BetterMap';
import ArrowBackIcon from 'common/components/icons/ArrowBackIcon';
import BugsnagService from 'common/services/BugsnagService';
import PlacesService from 'common/services/PlacesService';
import {positionBounds} from 'common/utils';
import {checkCoords, closestAddress} from 'common/utils/geo';
import CenteredMapMarker from 'ondemand/components/CenteredMapMarker';
import CornerButton from 'ondemand/components/CornerButton';
import DateTimeModal, {CURRENT_TIME} from 'ondemand/components/DateTimeModal';
import EligibilityLimitsAlert from 'ondemand/components/EligibilityLimitsAlert';
import FloatingBarLayout from 'ondemand/components/FloatingBarLayout';
import OndemandMap from 'ondemand/components/OndemandMap';
import PassengerDetailsInput from 'ondemand/components/PassengerDetailsInput';
import PaymentModal from 'ondemand/components/PaymentModal';
import RideDetailsInput from 'ondemand/components/RideDetailsInput';
import RideDirectionsMapLayer from 'ondemand/components/RideDirectionsMapLayer';
import RideMarker from 'ondemand/components/RideMarker';
import RidePrice from 'ondemand/components/RidePrice';
import RideRequestButton from 'ondemand/components/RideRequestButton';
import RideRequestErrorDialog from 'ondemand/components/RideRequestErrorDialog';
import RideRequestSearch from 'ondemand/components/RideRequestSearch';
import RideRequestStepper from 'ondemand/components/RideRequestStepper';
import ServiceAnnouncementsAlert from 'ondemand/components/ServiceAnnouncementsAlert';
import ServiceLoadControl from 'ondemand/components/ServiceLoadControl';
import TokenTransitDeeplinkModal from 'ondemand/components/TokenTransitDeeplinkModal';
import UserLocation from 'ondemand/components/UserLocation';
import UserLocationControl from 'ondemand/components/UserLocationControl';
import UserLocationMarker from 'ondemand/components/UserLocationMarker';
import messages from 'ondemand/messages.json';
import RideShareNotificationModal from './RideShareNotificationModal/RideShareNotificationModal';


const DEFAULT_ZOOM = 17;
const CUSTOM_PICKUP_NAME = 'Custom Pickup';
const CUSTOM_DROPOFF_NAME = 'Custom Drop-off';
const CURRENT_LOCATION_NAME = 'Current Location';
const ADDRESS_TYPE_AGENCY = 'agency';
const VALID_ADDRESS_DISTANCE = 25; // Valid distance from an address (meters)
// The possible ways to choose a pickup/dropoff address.
// We are tracking this in segment to learn more about how users choose addresses.
const SELECTION_TYPES = {
  DEFAULT: 'default',
  MANUAL: 'manual',
  USER_LOCATION: 'user_location',
  SEARCH_BAR: 'search_bar',
  ADDRESS_ICON: 'address_icon',
};

const bugsnagService = new BugsnagService();
const placesService = new PlacesService();
const getLocationName = (location) =>
  placesService.geoLookup(location).then(
    (result) => {
      if (!_.size(result)) {
        bugsnagService.notify('EmptyGeolookupResults', `Unable to geocode ${location}`);
        return null;
      }
      return result[0].formatted_address;
    },
    (error) => {
      bugsnagService.notifyException(error, `Unable to geocode ${location}`);
      return null;
    }
  );

class RideRequestMap extends React.Component {
  static displayName = 'RideRequestMap';

  static propTypes = {
    addresses: PropTypes.array,
    dropoffAddresses: PropTypes.array,
    pickupAddresses: PropTypes.array,
    dropoffArea: PropTypes.object,
    pickupArea: PropTypes.object,
    serviceArea: PropTypes.object,
    agency: PropTypes.object,
    agencyAddresses: PropTypes.array,
    agencySettings: PropTypes.object,
    allowBookForNow: PropTypes.bool.isRequired,
    allowBookForLater: PropTypes.bool.isRequired,
    center: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }),
    disabled: PropTypes.bool,
    onRideRequestChange: PropTypes.func,
    onRideRequestClear: PropTypes.func,
    onRideRequestSubmit: PropTypes.func,
    rideRequest: PropTypes.shape({
      pickup: PropTypes.object,
      pickupName: PropTypes.string,
      pickupDateTime: PropTypes.object,
      dropoff: PropTypes.object,
      dropoffName: PropTypes.string,
      capacity: PropTypes.number,
      wheelchair: PropTypes.bool,
      bicycle: PropTypes.bool,
      isConfirmed: PropTypes.bool,
      isFetching: PropTypes.bool,
      errors: PropTypes.array,
      payment: PropTypes.object,
    }).isRequired,
    rideRequestDirections: PropTypes.object,
    service: PropTypes.object,
    serviceAvailabilityMessage: PropTypes.node,
    serviceLoad: PropTypes.string,
    thresholds: PropTypes.object.isRequired,
    timezone: PropTypes.string,
    user: PropTypes.object,
    showRideShareNotification: PropTypes.bool,
    onRideShareConfirm: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this._mapCenter = props.center || {lat: 0, lng: 0};
    this._mapZoom = DEFAULT_ZOOM;

    this.state = {
      name: null,
      bounds: null,
      capacity: 1,
      center: this._mapCenter,
      pickupDateTime: _.all([
        props.allowBookForNow,
        !props.allowBookForLater,
      ]) ? CURRENT_TIME : null,
      wheelchair: false,
      bicycle: false,
      userLocation: null,
      pickupZoom: null,
      dropoffZoom: null,
      showPayment: false,
      ttneedsAcceptance: false,
      pickupSelectionType: SELECTION_TYPES.DEFAULT,
      dropoffSelectionType: SELECTION_TYPES.DEFAULT,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.state.pickupDateTime === null) {
      if (this.props.allowBookForNow && !this.props.allowBookForLater) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({pickupDateTime: CURRENT_TIME});
      }
    }

    if (this.props.center !== prevProps.center) {
      this._mapCenter = this.props.center;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({center: this.props.center});
    }

    if (this.props.rideRequest.errors) {
      const ttneedsAcceptance = this.props.rideRequest.errors.reduce((needsAcceptance, error) => {
        return needsAcceptance || error.parameter === 'payment.accepted_latest_terms';
      }, false);
      if (ttneedsAcceptance && !this.state.ttneedsAcceptance) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ttneedsAcceptance});
      }

      // If we had problems with the stripe token, ensure we clear it out
      const stripeTokenError = this.props.rideRequest.errors.reduce((hasTokenError, error) => {
        return hasTokenError || error.parameter === 'payment.stripe_token';
      }, false);

      if (stripeTokenError && this.props.rideRequest.payment &&
          this.props.rideRequest.payment.stripe_token) {
        this.props.onRideRequestChange({payment: null});
      }
    }
  }

  _handleBoundsChanged = (mapBounds) => {
    this._mapBounds = mapBounds;
  };

  _handleCenterChanged = (center) => {
    this._mapCenter = center;
  };

  _handleDragend = () => {
    const selectionType = this._getStep() === 2 ? 'pickupSelectionType' : 'dropoffSelectionType';
    this.setState({
      name: null,
      [selectionType]: SELECTION_TYPES.MANUAL,
    });
  };

  _handleZoomChanged = (zoom) => {
    const selectionType = this._getStep() === 2 ? 'pickupSelectionType' : 'dropoffSelectionType';
    this._mapZoom = zoom;
    this.setState({
      name: null,
      [selectionType]: SELECTION_TYPES.MANUAL,
    });
  };

  _handlePickupChange = () => {
    this.props.onRideRequestChange({
      pickup: this._mapCenter,
      pickupName: this.state.name || CUSTOM_PICKUP_NAME,
    });
    const {dropoff, dropoffName} = this.props.rideRequest;
    // Happens when a user has set a pickup and dropoff, then goes back to edit them
    if (dropoff) {
      const pickupZoom = this._mapZoom;
      this._mapZoom = this.state.dropoffZoom;
      this.props.onRideRequestChange({dropoff: null, dropoffName: null});
      this.setState({center: dropoff, pickupZoom});
      // Set name after possible zoomChanged event clears it out
      return _.defer(() => this.setState({name: dropoffName}));
    }
    const top = this._mapBounds ? this._mapBounds.ne.lat : 0;
    const bottom = this._mapBounds ? this._mapBounds.sw.lat : 0;
    // Shift the map downward 10% of it's height to reveal the pickup marker
    return this.setState({
      center: {
        lat: this._mapCenter.lat - (top - bottom) / 10,
        lng: this._mapCenter.lng,
      },
      name: null,
      pickupZoom: this._mapZoom,
    });
  };

  _handleDropoffChange = () => {
    const {pickup, pickupName} = this.props.rideRequest;
    const dropoff = this._mapCenter;
    const dropoffName = this.state.name || CUSTOM_DROPOFF_NAME;
    this.props.onRideRequestChange({dropoff, dropoffName});
    if (pickupName === CUSTOM_PICKUP_NAME || pickupName === CURRENT_LOCATION_NAME) {
      getLocationName(pickup).then(name =>
        this.props.onRideRequestChange({pickupName: name || pickupName}));
    }
    if (dropoffName === CUSTOM_DROPOFF_NAME || dropoffName === CURRENT_LOCATION_NAME) {
      getLocationName(dropoff).then(name =>
        this.props.onRideRequestChange({dropoffName: name || dropoffName}));
    }
    _.delay(
      () => {
        this.setState({
          bounds: positionBounds([
            this.props.rideRequest.pickup,
            dropoff,
            this.props.rideRequestDirections.pickup.position,
            this.props.rideRequestDirections.dropoff.position,
          ]),
          center: null,
          dropoffZoom: this._mapZoom,
        });
      },
      200
    );
  };

  _handlePassengerDetailsChange = () => {
    this.props.onRideRequestChange({
      capacity: this.state.capacity,
      wheelchair: this.state.wheelchair,
      bicycle: this.state.bicycle,
    });
  };

  _handleCapacityChange = (capacity) => {
    this.setState({capacity});
  };

  _setPickupDateTime = (dt) => {
    const {pickup, pickupName} = this.props.rideRequest;
    if (pickup) {
      // If the user edits the pickup time after setting a pickup location,
      // clear the pickup location from the ride request.
      this.props.onRideRequestChange({
        pickup: null,
        pickupName: null,
        pickupDateTime: dt === CURRENT_TIME ? null : dt,
      });
      // However, keep the map centered on that pickup.
      this.setState({
        bounds: null,
        center: pickup,
        name: pickupName,
        pickupDateTime: dt,
      });
    } else {
      this.props.onRideRequestChange({
        pickupDateTime: dt === CURRENT_TIME ? null : dt,
      });
      this.setState({
        pickupDateTime: dt,
      });
    }
  };

  _handleWheelchairChange = (wheelchair) => {
    this.setState({wheelchair});
  };

  _handleBicycleChange = (bicycle) => {
    this.setState({bicycle});
  };

  _handleAddressClick = ({name, address, position, type}) => {
    const newName = type === ADDRESS_TYPE_AGENCY ? name : address;
    const selectionType = this._getStep() === 2 ? 'pickupSelectionType' : 'dropoffSelectionType';
    this.setState({
      center: position,
      name: newName,
      [selectionType]: SELECTION_TYPES.ADDRESS_ICON,
    });
  };

  _handleSearchSuggestion = ({name, address, position, source}) => {
    const selectionType = this._getStep() === 2 ? 'pickupSelectionType' : 'dropoffSelectionType';
    position().then((center) =>
      this.setState({
        center,
        name: source === ADDRESS_TYPE_AGENCY ? name : address,
        [selectionType]: SELECTION_TYPES.SEARCH_BAR,
      })
    );
  };

  _handleUserLocationChanged = (position) => {
    this.setState({userLocation: position});
  };

  _moveToUserLocation = () => {
    const selectionType = this._getStep() === 2 ? 'pickupSelectionType' : 'dropoffSelectionType';
    this.setState({
      center: this.state.userLocation,
      name: CURRENT_LOCATION_NAME,
      [selectionType]: SELECTION_TYPES.USER_LOCATION,
    });
  };

  _handlePickupSuggestion = (suggestion) => {
    suggestion.position().then((position) =>
      this.setState({center: position})
    );
  };

  _handleConfirmClick = () => {
    if (this.props.service.fare_required) {
      this.setState({showPayment: true});
    } else {
      this.props.onRideRequestSubmit();
    }
  };

  _handlePaymentClose = () => {
    this.setState({showPayment: false});
  };

  _handleCash = () => {
    // Set the payment to null so we never send token information when trying to pay on vehicle
    this.props.onRideRequestChange({payment: null});
    _.defer(() => this.props.onRideRequestSubmit());
    this.setState({showPayment: false, ttneedsAcceptance: false});
  };

  _handleToken = (payment) => {
    this.props.onRideRequestChange({payment});
    _.defer(() => this.props.onRideRequestSubmit());
    // Hide the payment modal in case the ride submission fails and an error modal appears
    this.setState({showPayment: false, ttneedsAcceptance: false});
  };

  _handleDeepLinkConfirmation = () => {
    this.setState({showPayment: false});
    this.props.onRideRequestSubmit();
  }

  _removeService = () => {
    this.props.onRideRequestClear();
  };

  _removePickup = () => {
    const {pickup, pickupName} = this.props.rideRequest;
    this._mapZoom = this.state.pickupZoom;
    this.setState({bounds: null, center: pickup, name: pickupName});
    this.props.onRideRequestChange({pickup: null, pickupName: null});
    // Set name after possible zoomChanged event clears it out
    _.defer(() => this.setState({name: pickupName}));
  };

  _removeDropoff = () => {
    const {dropoff, dropoffName} = this.props.rideRequest;
    this._mapZoom = this.state.dropoffZoom;
    _.defer(() => this.setState({bounds: null, center: dropoff}));
    this.props.onRideRequestChange({dropoff: null, dropoffName: null});
    // Set name after possible zoomChanged event clears it out
    _.defer(() => this.setState({name: dropoffName}));
  };

  _removeLocations = () => {
    const {pickup, pickupName} = this.props.rideRequest;
    this._mapZoom = this.state.pickupZoom;
    this.props.onRideRequestChange({pickup: null, pickupName: null});
    _.defer(() => this.setState({bounds: null, center: pickup}));
    // Set name after possible zoomChanged event clears it out
    _.defer(() => this.setState({name: pickupName}));
  };

  _removePassengerDetails = () => {
    this.props.onRideRequestChange({
      capacity: null,
      wheelchair: null,
      bicycle: null,
    });
  };

  _removePickupDateTime = () => {
    // TODO:
    // Currently this completely removes the pickup time,
    // so the user has to enter everything again.
    // We should refactor to allow the current value to be the initial value
    // for when the user revisits the date/time selection modal.
    this.setState({pickupDateTime: null});
    this.props.onRideRequestChange({pickupDateTime: null});
  };

  _dismissError = () => {
    const center = this.props.rideRequest.pickup;
    this.props.onRideRequestChange({pickup: null, errors: null});
    _.defer(() => this.setState({bounds: null, center}));
  };

  _dismissTokenTransitError = () => {
    this.props.onRideRequestChange({errors: null});
  }

  _getStep = () => {
    const checkList = [
      // 0: The service is not available.
      () => _.any([
        this.props.disabled,
        !this.props.service,
        !this.props.allowBookForNow && !this.props.allowBookForLater,
      ]),

      // 1: User must choose a pickup time.
      () => this.state.pickupDateTime === null,

      // 2: User must choose pickup location.
      () => this.props.rideRequest.pickup === null,

      // 3: User must choose dropoff location.
      () => this.props.rideRequest.dropoff === null,

      // 4: User must set ride capacity & wheelchair access.
      () => _.any([
        this.props.rideRequest.capacity === null,
        this.props.rideRequest.wheelchair === null,
        this.props.rideRequest.bicycle === null,
      ]),

      // 5: User must confirm all ride details.
      () => !this.props.rideRequest.isConfirmed,

      // 6: Show ride details after it is confirmed.
      () => true,
    ];
    return _.findIndex(checkList, (f) => f());
  };

  _validPickup = () => {
    const validRegion = this.props.pickupArea &&
      this.props.rideRequest.pickup &&
      checkCoords(this.props.rideRequest.pickup, this.props.pickupArea);
    const validAddress = this.props.pickupAddresses &&
      this.props.rideRequest.pickup &&
      closestAddress(
        {position: this.props.rideRequest.pickup},
        _.filter(this.props.pickupAddresses, (a) => a.type !== 'rider')
      ).dist <= VALID_ADDRESS_DISTANCE;
    return validRegion || validAddress;
  };

  _validDropoff = () => {
    const validRegion = this.props.dropoffArea &&
      this.props.rideRequest.dropoff &&
      checkCoords(this.props.rideRequest.dropoff, this.props.dropoffArea);
    const validAddress = this.props.dropoffAddresses &&
      this.props.rideRequest.dropoff &&
      closestAddress(
        {position: this.props.rideRequest.dropoff},
        this.props.dropoffAddresses
      ).dist <= VALID_ADDRESS_DISTANCE;
    return validRegion || validAddress;
  };

  _getSelectableArea = (step) => {
    if (step === 2) {
      return this.props.pickupArea;
    } else if (step === 3) {
      return this.props.dropoffArea;
    }
    return this.props.serviceArea;
  };

  _renderFareModal = () => {
    if (this.props.service.token_transit_use_deeplink) {
      return <TokenTransitDeeplinkModal
        onClose={this._handlePaymentClose}
        onConfirm={this._handleDeepLinkConfirmation}
      />;
    }
    return (
      <PaymentModal
        capacity={this.state.capacity}
        existingToken={
          this.props.rideRequest.payment ?
          this.props.rideRequest.payment.stripe_token :
          null
        }
        service={this.props.service}
        publishableKey={this.props.agencySettings.stripePublishableKey}
        onClose={this._handlePaymentClose}
        onCash={this._handleCash}
        onToken={this._handleToken}
        tokenTransitPayment={!!this.props.agencySettings.token_transit_connected}
        ttneedsAcceptance={this.state.ttneedsAcceptance}
      />
    );
  }

  render() {
    if (this.props.service === null) return null;

    const step = this._getStep();

    const controlsStepperNode = (
      <RideRequestStepper step={step}>
        <div data-id="service-unavailable-section">
          <RideRequestButton label="SELECT PICKUP" disabled />
        </div>
        <div data-id="in-advance-section">
          <RideRequestButton label="SELECT PICKUP" disabled />
          <DateTimeModal
            allowBookForNow={this.props.allowBookForNow}
            allowBookForLater={this.props.allowBookForLater}
            initialValue={this.state.pickupDateTime}
            onBack={this._removeService}
            onContinue={this._setPickupDateTime}
            message={this.props.serviceAvailabilityMessage}
            service={this.props.service}
            timezone={this.props.agency.timezone}
          />
        </div>
        <div>
          <RideRequestButton
            data-id="pickup-submit"
            color="primary"
            label="SELECT PICKUP"
            onClick={this._handlePickupChange}
          />
        </div>
        <div>
          <RideRequestButton
            data-id="dropoff-submit"
            color="primary"
            label="SELECT DROP-OFF"
            onClick={this._handleDropoffChange}
          />
        </div>
        <div data-id="passenger-details-container">
          <PassengerDetailsInput
            capacity={this.state.capacity}
            wheelchair={this.state.wheelchair}
            bicycle={this.state.bicycle}
            onCapacityChange={this._handleCapacityChange}
            onWheelchairChange={
              (this.props.service &&
                this.props.service.wheelchair_accessible &&
                this._handleWheelchairChange) ||
                null
            }
            onBicycleChange={
              (this.props.service &&
                this.props.service.accommodate_bicycle &&
                this._handleBicycleChange) ||
                null
            }
            agencySettings={this.props.agencySettings}
          />
          <RideRequestButton
            data-id="next-button"
            color="primary"
            label="NEXT"
            onClick={this._handlePassengerDetailsChange}
          />
        </div>
        <div data-id="ride-details-container">
          <RideDetailsInput
            agency={this.props.agency}
            onLocationsEdit={this._removeLocations}
            onPassengerDetailsEdit={this._removePassengerDetails}
            onPickupDateTimeEdit={this.props.allowBookForLater ? this._removePickupDateTime : null}
            ride={this.props.rideRequest}
            thresholds={this.props.thresholds}
            timezone={this.props.timezone}
          />
          <RideRequestButton
            color="primary"
            dataId={'confirm-ride-button'}
            label={this.state.pickupDateTime === CURRENT_TIME ? 'CONFIRM RIDE' : 'SCHEDULE RIDE'}
            onClick={this._handleConfirmClick}
            disabled={this.props.rideRequest.isFetching}
          />
          {this.state.showPayment && this._renderFareModal()}
          {this.props.showRideShareNotification &&
            <RideShareNotificationModal
              serviceName={this.props.service.name}
              onAccept={this.props.onRideShareConfirm}
            />
          }
        </div>
      </RideRequestStepper>
    );

    const disablePickupGoogle = this.props.pickupArea &&
      this.props.pickupArea.coordinates &&
      !this.props.pickupArea.coordinates.length;
    const disableDropoffGoogle = this.props.dropoffArea &&
      this.props.dropoffArea.coordinates &&
      !this.props.dropoffArea.coordinates.length;

    const searchStepperNode = (
      <RideRequestStepper step={step}>
        <div />
        <div />
        <RideRequestSearch
          addresses={this.props.pickupAddresses}
          disableGoogleSource={disablePickupGoogle}
          onScheduleClick={this.props.allowBookForLater ? this._removePickupDateTime : null}
          onSuggestion={this._handleSearchSuggestion}
          placeholder="Search for pickup"
          value={this.state.name || CUSTOM_PICKUP_NAME}
        />
        <RideRequestSearch
          addresses={this.props.dropoffAddresses}
          disableGoogleSource={disableDropoffGoogle}
          onScheduleClick={this.props.allowBookForLater ? this._removePickupDateTime : null}
          onSuggestion={this._handleSearchSuggestion}
          placeholder="Search for drop-off"
          value={this.state.name || CUSTOM_DROPOFF_NAME}
        />
      </RideRequestStepper>
    );

    const pickupMarkers = (
      <AddressMarkers addresses={this.props.pickupAddresses} onClick={this._handleAddressClick} />
    );
    const dropoffMarkers = (
      <AddressMarkers addresses={this.props.dropoffAddresses} onClick={this._handleAddressClick} />
    );

    let userLocationMarker = null;
    let userLocationControl = null;
    if (this.state.userLocation !== null) {
      userLocationMarker = <UserLocationMarker position={this.state.userLocation} />;
      userLocationControl = (
        <Control position={ControlPosition.RIGHT_BOTTOM} style={{padding: '8px'}}>
          <UserLocationControl onClick={this._moveToUserLocation} />
        </Control>
      );
    }

    const serviceLoadControl = this.state.pickupDateTime !== CURRENT_TIME ? null : (
      <Control
        position={ControlPosition.RIGHT_BOTTOM}
        style={{padding: '8px 16px', pointerEvents: 'none'}}
      >
        <ServiceLoadControl load={this.props.serviceLoad} thresholds={this.props.thresholds} />
      </Control>
    );

    let errorDialog = null;
    if (this.props.rideRequest.pickup && !this._validPickup()) {
      errorDialog = (
        <RideRequestErrorDialog
          message={messages.pickupOutsideArea}
          onConfirm={this._removePickup}
        />
      );
    } else if (this.props.rideRequest.dropoff && !this._validDropoff()) {
      errorDialog = (
        <RideRequestErrorDialog
          message={messages.dropoffOutsideArea}
          onConfirm={this._removeDropoff}
        />
      );
    } else if (this.props.rideRequest.errors) {
      const onlyTokenError = this.props.rideRequest.errors.length === 1 &&
        this.props.rideRequest.errors[0].parameter === 'payment.accepted_latest_terms';

      const message = _.map(this.props.rideRequest.errors,
        (e) => <p key={e.description}>{e.description}</p>);

      // If the service requires a fare and we failed due to an invalid_parameter error,
      // indicate to the user that they have not been charged
      if (this.props.service.fare_required) {
        const invalidParameterError = this.props.rideRequest.errors.reduce((invalid, error) => {
          return invalid || error.code === 'invalid_parameter';
        }, false);
        if (invalidParameterError) {
          message.push(<p key={'no-charge'}>You have not been charged.</p>);
        }
      }

      errorDialog = (
        <RideRequestErrorDialog
          message={<div>{message}</div>}
          onConfirm={onlyTokenError ? this._dismissTokenTransitError : this._dismissError}
          title="Unable to book ride"
        />
      );
    }

    const selectableArea = this._getSelectableArea(step);
    return (
      <div style={{height: '100%'}}>
        {errorDialog}
        {searchStepperNode}
        <RidePrice capacity={this.state.capacity} service={this.props.service} />
        <UserLocation onLocationChanged={this._handleUserLocationChanged} />
        <ServiceAnnouncementsAlert
          announcements={this.props.service.announcements}
          paddingTop={'10px'}
        />
        <EligibilityLimitsAlert service={this.props.service} />
        <FloatingBarLayout bottom barContent={controlsStepperNode} desktopMax={400} mobileMax={756}>
          <div data-id="ride-request-map" style={{height: '100%'}}>
            <OndemandMap
              bounds={this.state.bounds}
              center={this.state.center}
              onBoundsChanged={this._handleBoundsChanged}
              onCenterChanged={this._handleCenterChanged}
              onDragend={this._handleDragend}
              onZoomChanged={this._handleZoomChanged}
              zoom={this._mapZoom}
            >
              {selectableArea &&
                <Geometry
                  geoJson={[selectableArea]}
                  fillOpacity={0.3}
                  fillColor={'#37474F'}
                  strokeOpacity={1}
                  strokeWeight={5}
                  strokeColor={'#FFFFFF'}
                  invert
                />
              }
              <RideRequestStepper step={step}>
                <div />
                <div />
                <div>
                  <CenteredMapMarker type="pickup" />
                  {pickupMarkers}
                  {userLocationControl}
                  {userLocationMarker}
                  {serviceLoadControl}
                </div>
                <div>
                  <CornerButton onClick={this._removePickup} data-id="ride-request-back-button">
                    <ArrowBackIcon />
                  </CornerButton>
                  <CenteredMapMarker type="dropoff" />
                  <RideMarker position={this.props.rideRequest.pickup} type="pickup" />
                  {dropoffMarkers}
                  {userLocationControl}
                  {userLocationMarker}
                  {serviceLoadControl}
                </div>
                <div>
                  <CornerButton onClick={this._removeDropoff} data-id="ride-request-back-button">
                    <ArrowBackIcon />
                  </CornerButton>
                  {this.props.rideRequestDirections &&
                    <RideDirectionsMapLayer {...this.props.rideRequestDirections} />}
                  {serviceLoadControl}
                </div>
                <div>
                  <CornerButton
                    onClick={this._removePassengerDetails}
                    data-id="ride-request-back-button"
                  >
                    <ArrowBackIcon />
                  </CornerButton>
                  {this.props.rideRequestDirections &&
                    <RideDirectionsMapLayer {...this.props.rideRequestDirections} />}
                  {serviceLoadControl}
                </div>
                <div>
                  {this.props.rideRequestDirections &&
                    <RideDirectionsMapLayer {...this.props.rideRequestDirections} />}
                  {serviceLoadControl}
                </div>
              </RideRequestStepper>
            </OndemandMap>
          </div>
        </FloatingBarLayout>
      </div>
    );
  }
}

export default RideRequestMap;



// WEBPACK FOOTER //
// ./src/ondemand/components/RideRequestMap.js